import { useQuery } from '@apollo/client';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { useSearch } from '@tanstack/react-router';
import { orderBy } from 'lodash';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import allViews from 'src/api/viewModels/allViews';
import DateRangeFilter from 'src/components/DateRangeFilter/DateRangeFilter';
import { cleanPayload } from 'src/helpers/common';
import { matchesFieldScope } from 'src/helpers/scope';
import type { BaseSearchParams } from 'src/utils/searchSchemas';
import useStyles from './CommandPaletteFilterStyles';

interface FacetEdge {
  key: string;
  doc_count: number;
  label?: string;
}

interface CategoryItem {
  id: string;
  slug: string;
  name: string;
  scope?: string[];
  sequence: number;
}

interface CommandPaletteFilterProps {
  selectedCategory: { id: string; name: string; label: string };
  onSelectCategory: (id: string, name: string, label: string) => void;
  categoryExclude: string[];
  facets: FacetEdge[] | null;
  facetsLoading: boolean;
  onSelectValue: (value: string) => void;
  onRemoveValue: (value: string) => void;
  appliedValues: string[];
  onBack: () => void;
  isDateCategory?: boolean;
  fieldType?: string;
  dateDisplayStyle?: string;
  existingRange?: { from: string; to: string } | null;
  onApplyRange?: (from: string, to: string) => void;
}

export default function CommandPaletteFilter({
  selectedCategory,
  onSelectCategory,
  categoryExclude,
  facets,
  facetsLoading,
  onSelectValue,
  onRemoveValue,
  appliedValues,
  onBack,
  isDateCategory,
  fieldType,
  dateDisplayStyle,
  existingRange,
  onApplyRange,
}: CommandPaletteFilterProps): React.ReactElement {
  const classes = useStyles();
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dateRangeMode, setDateRangeMode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isValuePhase = selectedCategory.id !== '';

  const { data: categoryData, loading: categoriesLoading } = useQuery(allViews, {
    variables: { viewName: 'Library', componentName: 'FilterBarFirstDropdown' },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  });

  const categories = useMemo(() => {
    if (!categoryData) return [];
    const cleaned = cleanPayload(categoryData) as {
      viewfieldSet?: {
        edges: Array<{
          node: { field: { id: string; slug: string; name: string; scope?: string[] }; sequence: number };
        }>;
      };
    }[];
    const vm = cleaned[0];
    if (!vm?.viewfieldSet?.edges) return [];
    return orderBy(
      vm.viewfieldSet.edges
        .filter((e) => !categoryExclude.some((word) => e.node.field?.slug?.toLowerCase() === word.toLowerCase()))
        .filter((e) => matchesFieldScope(e.node.field.scope, search.scope))
        .map((e) => ({
          id: e.node.field.id,
          slug: e.node.field.slug,
          name: e.node.field.name,
          scope: e.node.field.scope,
          sequence: e.node.sequence,
        })),
      ['sequence'],
      ['asc'],
    ) as CategoryItem[];
  }, [categoryData, categoryExclude, search.scope]);

  const filteredItems = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (isValuePhase) {
      if (!facets) return [];
      return q ? facets.filter((f) => f.key.toLowerCase().includes(q) || f.label?.toLowerCase().includes(q)) : facets;
    }
    return q ? categories.filter((c) => c.name.toLowerCase().includes(q)) : categories;
  }, [query, isValuePhase, facets, categories]);

  // Reset search and highlight when switching phases.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on phase change
  useEffect(() => {
    setQuery('');
    setHighlightedIndex(-1);
    setDateRangeMode(false);
    inputRef.current?.focus();
  }, [isValuePhase]);

  // Clamp highlight index when list shrinks
  useEffect(() => {
    if (highlightedIndex >= filteredItems.length) {
      setHighlightedIndex(Math.max(0, filteredItems.length - 1));
    }
  }, [filteredItems.length, highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < 0 ? 0 : Math.min(prev + 1, filteredItems.length - 1)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredItems.length > 0 && highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          if (isValuePhase) {
            const facet = filteredItems[highlightedIndex] as FacetEdge;
            if (appliedValues.includes(facet.key)) {
              onRemoveValue(facet.key);
            } else {
              onSelectValue(facet.key);
            }
          } else {
            const cat = filteredItems[highlightedIndex] as CategoryItem;
            onSelectCategory(cat.id, cat.slug, cat.name);
          }
        }
        break;
      case 'Escape':
        if (isValuePhase) {
          e.preventDefault();
          onBack();
        }
        break;
      case 'Backspace':
        if (isValuePhase && query === '') {
          e.preventDefault();
          onBack();
        }
        break;
    }
  };

  const isLoading = isValuePhase ? facetsLoading : categoriesLoading;

  return (
    <Box className={classes.root} onKeyDown={handleKeyDown}>
      {isValuePhase && (
        <button type="button" className={classes.header} onClick={onBack}>
          <ArrowBackIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" className={classes.headerLabel}>
            {selectedCategory.label}
          </Typography>
        </button>
      )}

      <Box className={classes.inputWrapper}>
        <InputBase
          inputRef={inputRef}
          autoFocus
          placeholder={isValuePhase ? `Search ${selectedCategory.label.toLowerCase()}...` : 'Search filters...'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={classes.input}
          fullWidth
        />
      </Box>

      <Box className={classes.divider} />

      <Box className={classes.list} sx={dateRangeMode ? { maxHeight: 'none' } : undefined}>
        {isLoading ? (
          <Box className={classes.loadingRow}>
            <CircularProgress size={16} color="secondary" />
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box className={classes.emptyRow}>
            <Typography variant="caption" color="text.secondary">
              No results
            </Typography>
          </Box>
        ) : isValuePhase && isDateCategory && dateRangeMode ? (
          <Box sx={{ p: 1 }}>
            <DateRangeFilter
              facets={facets ?? []}
              loading={facetsLoading}
              fieldType={fieldType ?? 'DA'}
              dateDisplayStyle={dateDisplayStyle ?? 'iso'}
              existingRange={existingRange ?? null}
              onApplyRange={(from, to) => {
                onApplyRange?.(from, to);
                setDateRangeMode(false);
              }}
              onBack={() => setDateRangeMode(false)}
            />
          </Box>
        ) : isValuePhase ? (
          <>
            {isDateCategory && (
              <button
                type="button"
                className={`${classes.item} ${classes.itemApplied}`}
                onClick={() => setDateRangeMode(true)}
              >
                <span className={classes.itemLabel}>Select range...</span>
              </button>
            )}
            {(filteredItems as FacetEdge[]).map((facet, i) => {
              const isApplied = appliedValues.includes(facet.key);
              return (
                <button
                  type="button"
                  key={facet.key}
                  className={`${classes.item} ${i === highlightedIndex ? classes.itemHighlighted : ''} ${isApplied ? classes.itemApplied : ''}`}
                  onClick={() => (isApplied ? onRemoveValue(facet.key) : onSelectValue(facet.key))}
                >
                  <span className={classes.itemLabel}>{facet.label ?? facet.key}</span>
                  <span className={classes.itemMeta}>
                    {isApplied ? <CheckIcon sx={{ fontSize: 14 }} color="secondary" /> : facet.doc_count}
                  </span>
                </button>
              );
            })}
          </>
        ) : (
          (filteredItems as CategoryItem[]).map((cat, i) => (
            <button
              type="button"
              key={cat.id}
              className={`${classes.item} ${i === highlightedIndex ? classes.itemHighlighted : ''}`}
              onClick={() => onSelectCategory(cat.id, cat.slug, cat.name)}
            >
              <span className={classes.itemLabel}>{cat.name}</span>
            </button>
          ))
        )}
      </Box>
    </Box>
  );
}
